import { client } from 'src/enviroment/api/axios'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Query } from '../index'
import { HomeStat, HomeProject, ProjectsResponse, LocationProjectCount, TagDistribution } from 'src/types/home'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { useState } from 'react'
import { DEFAULT_TAKE } from 'src/enviroment/constants/constants'

export const homeRouterKeys = {
  stats: 'home.stats',
  projectsByLocation: 'home.projectsByLocation',
  tagsDistribution: 'home.tagsDistribution',
  projectList: 'home.projectList',
  recentProjects: 'home.recentProjects',
  featuredProjects: 'home.featuredProjects',
} as const

export const homeRouter = {
  stats: {
    useQuery: (opts?: Query<HomeStat>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<HomeStat>({
        queryKey: [homeRouterKeys.stats, selectedTenantId],
        queryFn: async () => {
          return (await client.get<HomeStat>(`/tenants/${selectedTenantId}/home:stats`)).data
        },
        enabled: Boolean(selectedTenantId),
        ...opts,
      })
    },
  },
  projectsByLocation: {
    useQuery: (opts?: Query<LocationProjectCount[]>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<LocationProjectCount[]>({
        queryKey: [homeRouterKeys.projectsByLocation, selectedTenantId],
        queryFn: async () => {
          return (await client.get<LocationProjectCount[]>(`/tenants/${selectedTenantId}/home:projects-by-location`))
            .data
        },
        enabled: Boolean(selectedTenantId),
        ...opts,
      })
    },
  },
  tagsDistribution: {
    useQuery: (opts?: Query<TagDistribution>) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<TagDistribution>({
        queryKey: [homeRouterKeys.tagsDistribution, selectedTenantId],
        queryFn: async () => {
          return (await client.get<TagDistribution>(`/tenants/${selectedTenantId}/home:tags-distribution`)).data
        },
        enabled: Boolean(selectedTenantId),
        ...opts,
      })
    },
  },
  projects: {
    useInfiniteQuery: () => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      const query = useInfiniteQuery({
        queryKey: [homeRouterKeys.projectList, selectedTenantId],
        queryFn: async ({ pageParam = null }) => {
          return (
            await client.get<HomeProject[]>(`/tenants/${selectedTenantId}/projects:filter`, {
              params: {
                cursorId: pageParam,
                take: 4,
              },
            })
          ).data
        },
        enabled: Boolean(selectedTenantId),
        getNextPageParam: (lastPage) => lastPage.at(-1)?.id,
        getPreviousPageParam: (firstPage) => firstPage.at(0)?.id,
      })

      return {
        ...query,
        hasPreviousPage: query.hasPreviousPage,
        fetchPreviousPage: () => query.fetchPreviousPage(),
      }
    },
  },
  recentProjects: {
    useQuery: (initialPageSize = DEFAULT_TAKE) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)
      const [page, setPage] = useState(1)

      const query = useQuery<ProjectsResponse>({
        queryKey: [homeRouterKeys.recentProjects, selectedTenantId, page, initialPageSize],
        queryFn: async () => {
          return (
            await client.get<ProjectsResponse>(`/tenants/${selectedTenantId}/projects:recent`, {
              params: {
                page,
                pageSize: initialPageSize,
              },
            })
          ).data
        },
        enabled: Boolean(selectedTenantId),
      })

      const totalPages = Math.ceil((query.data?.total || 0) / initialPageSize)

      return {
        ...query,
        page,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
        nextPage: () => setPage((old) => Math.min(old + 1, totalPages)),
        previousPage: () => setPage((old) => Math.max(old - 1, 1)),
        setPage: (newPage: number) => setPage(Math.min(Math.max(1, newPage), totalPages)),
      }
    },
  },
  featuredProjects: {
    useQuery: (initialPageSize = DEFAULT_TAKE) => {
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)
      const [page, setPage] = useState(1)

      const query = useQuery<ProjectsResponse>({
        queryKey: [homeRouterKeys.featuredProjects, selectedTenantId, page, initialPageSize],
        queryFn: async () => {
          return (
            await client.get<ProjectsResponse>(`/tenants/${selectedTenantId}/projects:featured`, {
              params: {
                page,
                pageSize: initialPageSize,
              },
            })
          ).data
        },
        enabled: Boolean(selectedTenantId),
      })

      const totalPages = Math.ceil((query.data?.total || 0) / initialPageSize)

      return {
        ...query,
        page,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
        nextPage: () => setPage((old) => Math.min(old + 1, totalPages)),
        previousPage: () => setPage((old) => Math.max(old - 1, 1)),
        setPage: (newPage: number) => setPage(Math.min(Math.max(1, newPage), totalPages)),
      }
    },
  },
}
